import { countryOfOriginOptions } from '@/utils/constants';
import { zodResolver } from '@hookform/resolvers/zod';
import { InfoOutlined } from '@mui/icons-material';
import { Box, Button, Grid, IconButton } from '@mui/material';
import { usePutOperatorsMutation } from 'api/queries/operators';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ClientType,
  ControlledField,
  PhoneInputField,
  SelectInputField,
  StyledTooltip,
  TextareaInputField,
  colors,
  removeEmptyStringKeys,
} from 'remote/global';
import PaperLayout from '../../PaperLayout';
import { OperatorDtoSchemaType } from '../types';
import { AccountCompanyDetailsSchemaType, accountCompanyDetailsSchema } from './types';

function AccountCompanyDetailsForm({ defaultValues }: { defaultValues?: OperatorDtoSchemaType }) {
  const tInstance = useTranslation();
  const { t } = tInstance;
  const {
    handleSubmit,
    control,
    setValue,
    formState: { isSubmitting, isValidating, isLoading },
  } = useForm<AccountCompanyDetailsSchemaType>({
    defaultValues: {
      address: '',
      brandName: '',
      companyVat: '',
      contactEmail: '',
      contactPhoneNumber: '',
      emergencyEmail: '',
      emergencyPhoneNumber: '',
      legalName: '',
      operatingLicense: '',
      regCode: '',
      website: '',
      description: '',
      offerContactEmail: '',
      offerContactPhone: '',
      type: ClientType.BUSINESS,
      ...defaultValues,
    },
    resolver: zodResolver(accountCompanyDetailsSchema),
  });
  const putOperatorsMutation = usePutOperatorsMutation();
  const onValid = (data: AccountCompanyDetailsSchemaType) =>
    putOperatorsMutation.mutate(removeEmptyStringKeys(data));

  // Just show the selected  country because the field is disabled
  const countryOptions =
    countryOfOriginOptions.filter((el) => el.value === defaultValues?.country) ?? [];

  return (
    <PaperLayout
      title={t('SettingsView.company.title')}
      subtitle={t('SettingsView.company.subtitle')}
      sx={{ maxWidth: 650 }}
    >
      <form onSubmit={handleSubmit(onValid)}>
        <Grid container item xs={12} columnSpacing={4}>
          <Grid item xs={12} sm={6}>
            <ControlledField control={control} name='companyVat' />
            <ControlledField control={control} name='legalName' />
            <ControlledField control={control} name='brandName' />
            <ControlledField
              control={control}
              name='country'
              render={(renderProps) => (
                <SelectInputField
                  {...renderProps}
                  options={countryOptions}
                  fieldProps={{ disabled: true }}
                />
              )}
              focusInitial
            />
            <ControlledField control={control} name='regCode' fieldProps={{ disabled: true }} />
            <ControlledField control={control} name='address' />
            <ControlledField control={control} name='operatingLicense' />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledField
              control={control}
              name='contactPhoneNumber'
              render={(renderProps) => <PhoneInputField {...renderProps} />}
            />
            <ControlledField
              control={control}
              name='contactEmail'
              fieldProps={{
                InputProps: {
                  type: 'email',
                  autoComplete: 'email',
                },
              }}
            />
            <ControlledField
              control={control}
              name='emergencyPhoneNumber'
              render={(renderProps) => <PhoneInputField {...renderProps} />}
            />
            <ControlledField
              control={control}
              name='emergencyEmail'
              fieldProps={{
                InputProps: {
                  type: 'email',
                  autoComplete: 'email',
                },
              }}
            />
            <ControlledField
              control={control}
              name='offerContactPhone'
              render={(renderProps) => (
                <PhoneInputField
                  {...renderProps}
                  fieldProps={{
                    onChange: (val, i) => {
                      if (val === `+${i.countryCallingCode}`) {
                        setValue('offerContactPhone', null);
                        return;
                      }
                      if (val?.length === 0) setValue('offerContactPhone', null);
                      else setValue('offerContactPhone', val);
                    },
                    label: (
                      <Box
                        component='span'
                        sx={{
                          verticalAlign: 'middle',
                          display: 'flex',
                          justifyContent: 'flex-start',
                        }}
                      >
                        {t('FormFields.offerContactPhone.label').toString()}{' '}
                        <StyledTooltip title={t('FormFields.offerContactPhone.tooltip')}>
                          <IconButton size='small'>
                            <InfoOutlined
                              fontSize='small'
                              sx={{ fontSize: '16px', color: colors.gray3, mt: -0.3, ml: 6 }}
                            />
                          </IconButton>
                        </StyledTooltip>
                      </Box>
                    ),
                  }}
                />
              )}
            />
            <ControlledField
              control={control}
              name='offerContactEmail'
              fieldProps={{
                InputProps: {
                  type: 'email',
                  autoComplete: 'email',
                },
                label: (
                  <Box component='span' sx={{ verticalAlign: 'middle' }}>
                    {t('FormFields.offerContactEmail.label').toString()}{' '}
                    <StyledTooltip title={t('FormFields.offerContactEmail.tooltip')}>
                      <IconButton size='small'>
                        <InfoOutlined
                          fontSize='small'
                          sx={{ fontSize: '16px', color: colors.gray3, mt: -0.3 }}
                        />
                      </IconButton>
                    </StyledTooltip>
                  </Box>
                ),
              }}
            />
          </Grid>
        </Grid>
        <ControlledField control={control} name='website' />
        <ControlledField
          control={control}
          name='description'
          render={(renderProps) => (
            <TextareaInputField
              fieldProps={{
                minRows: 2,
              }}
              {...renderProps}
            />
          )}
        />
        <Button
          type='submit'
          disabled={isSubmitting || isValidating || isLoading}
          variant='contained'
          fullWidth
          sx={{ mt: '2rem' }}
        >
          {t('save')}
        </Button>
      </form>
    </PaperLayout>
  );
}

export default AccountCompanyDetailsForm;
